import {browserFailuresIntegration} from "@finance/shared/observability_browser";
import * as Sentry from "@sentry/vue";
import {Capacitor} from "@capacitor/core";
import {isAppleSignInCancelled} from "./apple_sign_in";
import {consoleSandbox, diagnosticIdentifier, errorDiagnostics, glitchTipOptions, recordDiagnostic, reportError, reportUnhandledError, reportWarning, safeConsoleLoggingIntegration, type DiagnosticAttributes} from "@finance/shared/observability";
import {clientDiagnostics, initializeClientDiagnostics, observeScreen} from "./diagnostics";
import {MutationCache, QueryCache, QueryClient} from "@tanstack/vue-query";
import {queryKeys} from "@finance/shared";
import type {Router} from "vue-router";
import type {App} from "vue";

/** Capacitor's iOS bridge echoes a rejected plugin call to console.error; sign-in pages already treat a cancelled Apple sheet as expected. */
export const expectedConsoleOutput = (values: readonly unknown[]) => values.some(isAppleSignInCancelled);

const staleChunkReloadKey = "sprout.stale-chunk-reload";
let staleChunkReloadAt = Number.NEGATIVE_INFINITY;

/**
 * A page left open across a deploy still imports chunks the new release removed.
 * Reload once to fetch the current build; a repeat failure soon after is reported.
 */
export function reloadOnStaleChunk(event: Event): void {
  try {
    if (Date.now() - Number(sessionStorage.getItem(staleChunkReloadKey)) < 60_000) return;
    sessionStorage.setItem(staleChunkReloadKey, String(Date.now()));
  } catch {
    // Without storage a reload could repeat indefinitely; report the failure instead.
    return;
  }
  event.preventDefault();
  // The prevented import resolves to nothing, so its callers fail until the page unloads; the reload replaces them.
  staleChunkReloadAt = Date.now();
  window.location.reload();
}

/**
 * Drops follow-on failures of a page reloading onto the current release, which the
 * offline queue would otherwise resend. Bounded in case the reload never happens.
 */
export function dropWhileReloadingStaleChunk<T>(event: T): T | null {
  return Date.now() - staleChunkReloadAt < 10_000 ? null : event;
}

/**
 * Browser translators (Chrome/Edge) rewrite text nodes behind Vue and mark the root
 * element; tagging each event says whether a DOM failure ran on a translated page.
 */
export function tagTranslatedPage<T extends {tags?: Record<string, unknown>}>(event: T): T {
  const root = document.documentElement;
  const translated = root.classList.contains("translated-ltr") || root.classList.contains("translated-rtl") || (root.lang !== "" && !/^en(?:-|$)/i.test(root.lang));
  return {...event, tags: {...event.tags, translated}};
}

export function initializeObservability(): void {
  // Registered before the SDK so its preload listener sees the recovery.
  window.addEventListener("vite:preloadError", reloadOnStaleChunk);
  Sentry.init({
    ...glitchTipOptions(Capacitor.isNativePlatform() ? "ios-web" : "frontend", import.meta.env.MODE, import.meta.env.VITE_APP_RELEASE ?? "development", import.meta.env.VITE_GLITCHTIP_DSN),
    attachProps: false,
    transport: (options) => Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport)({...options, dbName: "sprout-diagnostics", maxQueueSize: 100, flushAtStartup: true}),
    integrations: (defaults) => [...defaults.filter((integration) => !["Dedupe", "Vue"].includes(integration.name)), Sentry.browserTracingIntegration(), safeConsoleLoggingIntegration(expectedConsoleOutput), browserFailuresIntegration()],
    // Do not propagate tracing headers to bank/provider origins.
    tracePropagationTargets: []
  });
  Sentry.getGlobalScope().addEventProcessor(dropWhileReloadingStaleChunk);
  Sentry.getGlobalScope().addEventProcessor(tagTranslatedPage);
  initializeClientDiagnostics();
}

export function observeVueApp(app: App, router: Router): void {
  Sentry.addIntegration(Sentry.vueIntegration({app, attachProps: false}));
  const previousWarningHandler = app.config.warnHandler;
  app.config.warnHandler = (message, instance, trace) => {
    reportWarning(new Error("Vue warning"), "vue.warning", {component: diagnosticIdentifier(instance?.$options.name) ?? "anonymous"});
    if (previousWarningHandler) previousWarningHandler(message, instance, trace);
    else consoleSandbox(() => console.warn(`[Vue warn]: ${message}${trace}`));
  };
  router.onError((error, to, from) => reportError(error, "router.navigation", {route: to.matched.at(-1)?.path ?? "unmatched", screen: String(to.name ?? "unknown"), previousScreen: String(from.name ?? "unknown")}));
  let enteredAt = performance.now();
  router.afterEach((to, from, failure) => {
    if (failure) {
      recordDiagnostic("navigation.cancelled", {screen: String(to.name ?? "unknown"), previousScreen: String(from.name ?? "unknown")});
      return;
    }
    const now = performance.now();
    observeScreen(String(to.name ?? to.matched.at(-1)?.path ?? "unmatched"), String(from.name ?? "startup"), Math.round(now - enteredAt));
    enteredAt = now;
  });
}

// Keys also contain account IDs and filters. Only their fixed namespace identifies the operation.
const operationNames = new Set<string>([...Object.values(queryKeys).map((key) => (typeof key === "function" ? key("", "")[0] : key[0])), "banks.connect", "banks.reconnect", "banks.disconnect", "engagement.check_in", "engagement.preferences", "subscription.purchase", "subscription.restore"]);
const operationName = (key: readonly unknown[] | undefined) => (key?.[0] === "subscription" && ["products", "entitlement", "offer"].includes(String(key[1])) ? `subscription.${String(key[1])}` : typeof key?.[0] === "string" && operationNames.has(key[0]) ? key[0] : "unnamed");

export function createObservedQueryClient(): QueryClient {
  const origins = new WeakMap<object, DiagnosticAttributes>();
  const queryCache = new QueryCache({
    onError: (error, query) => {
      const attributes = {...origins.get(query), ...errorDiagnostics(error), queryName: operationName(query.queryKey), failureCount: query.state.fetchFailureCount, outcome: "failed"};
      recordDiagnostic("query.failed", attributes);
      reportUnhandledError(error, "query.failed", attributes);
    },
    onSuccess: (_data, query) => recordDiagnostic("query.completed", {...origins.get(query), queryName: operationName(query.queryKey)})
  });
  queryCache.subscribe((event) => {
    if (event.type === "updated" && event.action.type === "fetch") origins.set(event.query, clientDiagnostics());
  });
  return new QueryClient({
    queryCache,
    mutationCache: new MutationCache({
      onMutate: (_variables, mutation) => {
        const attributes = clientDiagnostics();
        origins.set(mutation, attributes);
        recordDiagnostic("mutation.started", {...attributes, mutationName: operationName(mutation.options.mutationKey)});
      },
      onError: (error, _variables, _context, mutation) => {
        const attributes = {...origins.get(mutation), ...errorDiagnostics(error), mutationName: operationName(mutation.options.mutationKey), outcome: "failed"};
        recordDiagnostic("mutation.failed", attributes);
        reportUnhandledError(error, "mutation.failed", attributes);
      },
      onSuccess: (_data, _variables, _context, mutation) => recordDiagnostic("mutation.completed", {...origins.get(mutation), mutationName: operationName(mutation.options.mutationKey)})
    }),
    defaultOptions: {queries: {staleTime: 30_000, retry: 1}}
  });
}
